import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Timekeeper from 'react-timekeeper';
import { FaCalendarAlt, FaRegClock, FaTimes } from 'react-icons/fa';
import Select from 'common/components/select/Select';
import Button from 'common/components/button/Button';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import InputGroup from 'common/components/inputGroup/InputGroup';
import Label from 'common/components/label/Label';
import { TimeKeeperDoneButton } from 'common/components/timeKeeperDoneButton/TimeKeeperDoneButton';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { DATEPICKER_FORMAT_DEFAULT } from 'Config';
import PrivacyService from 'api/privacy/PrivacyService';
import DriversService from 'api/drivers/DriversService';
import VehiclesService from 'api/vehicles/VehiclesService';
import styles from '../PrivacyTab.module.scss';

type Props = {
    entityType: 'driver' | 'vehicle';
    onDeleted?: () => void;
};

// --- Inline time picker ---
type TimePickerInlineProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    removeInputBorderRadius?: boolean;
};

const formatTimeDisplay = (val: string) => {
    if (!val) return '';
    const [h, m] = val.split(':').map(Number);
    return `${(h || 0).toString().padStart(2, '0')}:${(m || 0).toString().padStart(2, '0')}`;
};

const TimePickerInline: React.FC<TimePickerInlineProps> = ({ value, onChange, placeholder, removeInputBorderRadius }: TimePickerInlineProps) => {
    const [showPicker, setShowPicker] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!showPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
                pickerRef.current && !pickerRef.current.contains(e.target as Node)
            ) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPicker]);

    const openPicker = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const PICKER_HEIGHT = 360;
            const openAbove = spaceBelow < PICKER_HEIGHT && rect.top > spaceBelow;
            setPickerPos({
                top: openAbove ? rect.top - PICKER_HEIGHT - 4 : rect.bottom + 4,
                left: rect.left,
            });
        }
        setShowPicker(true);
    };

    const parsed = value ? { hour: parseInt(value.split(':')[0]) || 0, minute: parseInt(value.split(':')[1]) || 0 } : undefined;

    return (
        <>
            <div ref={triggerRef}>
                <InputGroup
                    value={formatTimeDisplay(value)}
                    icon={<FaRegClock />}
                    readOnly
                    onClick={openPicker}
                    clickInText={true}
                    onClickInText={openPicker}
                    style={{ cursor: 'pointer' }}
                    placeholder={placeholder}
                    isClearable={!!value}
                    onClear={() => onChange('')}
                    removeInputBorderRadius={removeInputBorderRadius}
                />
            </div>
            {showPicker && ReactDOM.createPortal(
                <div
                    ref={pickerRef}
                    style={{ position: 'fixed', zIndex: 1100, top: pickerPos.top, left: pickerPos.left }}
                >
                    <Timekeeper
                        time={parsed}
                        hour24Mode={true}
                        closeOnMinuteSelect={true}
                        switchToMinuteOnHourSelect
                        doneButton={result => (
                            <TimeKeeperDoneButton onClick={() => {
                                setShowPicker(false);
                                if (result && result.formatted24) {
                                    onChange(result.formatted24);
                                }
                            }} />
                        )}
                    />
                </div>,
                document.body,
            )}
        </>
    );
};

// --- Main form ---
const DeleteDataForm: React.FC<Props> = ({ entityType, onDeleted }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [entities, setEntities] = useState<SelectValueLabel[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<SelectValueLabel[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [startHour, setStartHour] = useState('');
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [endHour, setEndHour] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadEntities = useCallback(async () => {
        try {
            if (entityType === 'driver') {
                const result = await DriversService.catalog();
                setEntities(result);
            } else {
                const result = await VehiclesService.catalog();
                setEntities(result);
            }
        } catch {
            // Error handled by Http interceptor
        }
    }, [entityType]);

    useEffect(() => {
        loadEntities();
    }, [loadEntities]);

    const combineDateHour = (date: Date | null, hour: string, isEnd = false): string | null => {
        if (!date) return null;
        const d = new Date(date);
        if (hour) {
            const [h, m] = hour.split(':').map(Number);
            d.setHours(h || 0, m || 0, 0, 0);
        } else {
            d.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, 0);
        }
        return d.toISOString();
    };

    const handleDelete = async () => {
        if (selectedEntities.length === 0) return;

        try {
            setIsDeleting(true);
            const start = combineDateHour(startDate, startHour);
            const end = combineDateHour(endDate, endHour, true);

            for (const entity of selectedEntities) {
                await PrivacyService.deleteData(entity.value, entity.label, entityType, start, end);
            }

            setSelectedEntities([]);
            setStartDate(null);
            setStartHour('');
            setEndDate(null);
            setEndHour('');
            setShowConfirm(false);
            addToast(t('privacy.anonymize_success'), { appearance: 'success' });
            onDeleted?.();
        } catch {
            // Error handled by Http interceptor
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancel = () => {
        setSelectedEntities([]);
        setStartDate(null);
        setStartHour('');
        setEndDate(null);
        setEndHour('');
    };

    const titleKey = entityType === 'driver' ? 'privacy.anonymize_driver_title' : 'privacy.delete_by_vehicle_title';
    const descKey = entityType === 'driver' ? 'privacy.anonymize_driver_desc' : 'privacy.delete_by_vehicle_desc';
    const actionKey = entityType === 'driver' ? 'privacy.anonymize_data' : 'privacy.delete_data';
    const selectPlaceholderKey = entityType === 'driver' ? 'privacy.select_driver_placeholder' : 'privacy.select_vehicle_placeholder';

    return (
        <>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>{t(titleKey) as string}</div>
                        <div className={styles.cardDesc}>
                            <span className={styles.warningInline}>&#9888;</span> {t(descKey) as string}. <span className={styles.warningInline}>{t('privacy.irreversible_action') as string}</span>
                        </div>
                    </div>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.deleteFormRow}>
                        <div className={styles.deleteFormField}>
                            <Label>{t(selectPlaceholderKey) as string}</Label>
                            <Select
                                options={entities}
                                value={selectedEntities}
                                onChange={(options: SelectValueLabel[]) => setSelectedEntities(options || [])}
                                placeholder={t(selectPlaceholderKey) as string}
                                isClearable
                                isMulti
                            />
                        </div>
                    </div>

                    <div className={styles.deleteFormRow}>
                        <div className={styles.deleteFormField}>
                            <Label>{t('privacy.start_date') as string}</Label>
                            <div className={styles.deleteFormDateHour}>
                                <div className={styles.deleteFormDateField}>
                                    <div className={styles.dateInputWrapper}>
                                        <DateTimePicker
                                            selected={startDate}
                                            onChange={date => setStartDate(date as Date)}
                                            dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                            placeholderText={t('privacy.start_date') as string}
                                            customInput={<InputGroup icon={<FaCalendarAlt />} clickInText={true} removeIconOrTextBorderRadius={true} placeholder={t('privacy.start_date') as string} />}
                                            popperProps={{ strategy: 'fixed' } as any}
                                        />
                                        {startDate && <span className={styles.dateClearBtn} onClick={() => setStartDate(null)}><FaTimes /></span>}
                                    </div>
                                </div>
                                <div className={styles.deleteFormTimeField}>
                                    <TimePickerInline
                                        value={startHour}
                                        onChange={setStartHour}
                                        placeholder={t('common.hour') as string}
                                        removeInputBorderRadius
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.deleteFormField}>
                            <Label>{t('privacy.end_date') as string}</Label>
                            <div className={styles.deleteFormDateHour}>
                                <div className={styles.deleteFormDateField}>
                                    <div className={styles.dateInputWrapper}>
                                        <DateTimePicker
                                            selected={endDate}
                                            onChange={date => setEndDate(date as Date)}
                                            dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                            placeholderText={t('privacy.end_date') as string}
                                            customInput={<InputGroup icon={<FaCalendarAlt />} clickInText={true} removeIconOrTextBorderRadius={true} placeholder={t('privacy.end_date') as string} />}
                                            minDate={startDate || undefined}
                                            popperProps={{ strategy: 'fixed' } as any}
                                        />
                                        {endDate && <span className={styles.dateClearBtn} onClick={() => setEndDate(null)}><FaTimes /></span>}
                                    </div>
                                </div>
                                <div className={styles.deleteFormTimeField}>
                                    <TimePickerInline
                                        value={endHour}
                                        onChange={setEndHour}
                                        placeholder={t('common.hour') as string}
                                        removeInputBorderRadius
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.deleteFormActions}>
                        <Button
                            text={t('common.cancel') as string}
                            preset="secondary"
                            size="small"
                            onClick={handleCancel}
                        />
                        <Button
                            text={t(actionKey) as string}
                            preset="danger"
                            size="small"
                            isDisabled={selectedEntities.length === 0 || !startDate || !endDate}
                            isLoading={isDeleting}
                            onClick={() => setShowConfirm(true)}
                        />
                    </div>
                </div>
            </div>

            <QuestionYesNo
                isVisible={showConfirm}
                message={t('privacy.confirm_delete_message') as string}
                onYes={handleDelete}
                onNo={() => setShowConfirm(false)}
            />
        </>
    );
};

export default DeleteDataForm;
